import { type Action } from 'react-sweet-state';

import { type DocNode } from '@atlaskit/adf-schema/doc';
import type {
	RovoApp,
	SourceMode,
} from '@atlassian/conversation-assistant-chat-prompt-input/controllers/chat-mode-store/types';
import type { BrowserContextSanitizer } from '@atlassian/conversation-assistant-chat-prompt-input/types';
import { type FollowUpSource } from '@atlassian/conversation-assistant-instrumentation/constants/analytics';
import type { AdditionalAnalyticAttributes } from '@atlassian/conversation-assistant-message-actions-api/types';
import {
	type AgentVersionInput,
	type AlphaExtendedThinkingMode,
	type ConversationChannelScope,
	type Dialogues,
	type LastAgentMessageStatus,
	type MessageType,
	type RovoLabsConfirmedTools,
} from '@atlassian/conversation-assistant-service-api/types/assistance-service';
import {
	type AgentMetadata,
	type AgentVersion,
	type CHAT_STORE_STATUS,
	type ChatStorePrivateState,
	type Conversation,
	type ConversationResultAttributionContext,
	type StoreChatItem,
} from '@atlassian/conversation-assistant-store/controllers/chat/types';

import { abortRequests } from './abortRequests';
import { addAgentMessage } from './addAgentMessage';
import { addErrorMessage } from './addErrorMessage';
import { answerNow } from './answerNow';
import { bulkResolveConversationActions } from './bulkResolveConversationActions';
import { bulkResolveConversationUIActions } from './bulkResolveConversationUIActions';
import { clearFollowUpMessages } from './clearFollowUpMessages';
import { clearTargetTaskMessageId } from './clearTargetTaskMessageId';
import { createConversationOptimistically } from './createConversationOptimistically';
import { createSeededConversation } from './createSeededConversation';
import { deleteConversation } from './deleteConversation';
import { fetchConversation } from './fetchConversation';
import { fetchConversationChannelsData } from './fetchConversationChannelsData';
import { handleChatOpenPubsubAction } from './handleChatOpenPubsubAction';
import { hydrateConversationWithExternalParams } from './hydrateConversationWithExternalParams';
import { loadMoreConversationChannelsData } from './loadMoreConversationChannelsData';
import { prefetchConversationMessages } from './prefetchConversationMessages';
import { type ReconnectToInProgressStreamParams } from './reconnect-action';
import { reconnectToInProgressStream } from './reconnectToInProgressStream';
import { removeConversationFromStore } from './removeConversationFromStore';
import { removeMessage } from './removeMessage';
import { resolveConversationAction } from './resolveConversationAction';
import { restoreTaskModeIfNeeded } from './restoreTaskModeIfNeeded';
import { seedConversation } from './seedConversation';
import { sendA2UIAction, type SendA2UIActionArgs } from './sendA2UIAction';
import { sendMessage } from './sendMessage';
import { type SendMessageConsumerPramas } from './sendMessageActionTypes';
import { sendMessageConfirmedTools } from './sendMessageConfirmedTools';
import { setConversationData } from './setConversationData';
import { setConversationListMounted } from './setConversationListMounted';
import { setSelectedConversation } from './setSelectedConversation';
import { stopTask } from './stopTask';
import { truncateFromLastHumanMessage } from './truncateFromLastHumanMessage';
import type {
	BulkResolveConversationActionsArgs,
	BulkResolveConversationUIActionsArgs,
	ChatStoreAction,
	ChatStoreContainerProps,
	HumanMessageIdReplacementListener,
	FetchConversationArgs,
	FetchConversationChannelsDataArgs,
	MessageSource,
	PrefetchConversationMessagesArgs,
	ResolveConversationActionArgs,
	WarmConversationCacheArgs,
} from './types';
import { updateConversation } from './updateConversation';
import { warmConversationCache } from './warmConversationCache';

const registerHumanMessageIdReplacementHandler =
	(
		handler: HumanMessageIdReplacementListener,
	): Action<ChatStorePrivateState, ChatStoreContainerProps, () => void> =>
	(_state, { registerHumanMessageIdReplacementHandler: registerHandler }) =>
		registerHandler?.(handler) ?? (() => {});

export const actions: {
	abortRequests: (
		args_0?:
			| {
					conversationId?: string;
					deepResearchEnabled?: boolean;
					thinkDeeperEnabled?: boolean;
					fastModeEnabled?: boolean;
					taskModeEnabled?: boolean;
			  }
			| undefined,
	) => Action<ChatStorePrivateState, ChatStoreContainerProps, Promise<void>>;
	addAgentMessage: (args_0: {
		agentNamedId: string;
		conversationId: string;
		content: string | DocNode;
	}) => Action<ChatStorePrivateState, ChatStoreContainerProps, Promise<void>>;
	addErrorMessage: (args_0: {
		error: unknown;
		conversationId?: string;
		errorPrefix?: string;
	}) => Action<ChatStorePrivateState, ChatStoreContainerProps, Promise<void>>;
	answerNow: (args_0: {
		conversationId?: string;
		tmpMessageId: string; // For store operations (finding/updating the message)
		remoteMessageId: string; // For the API call
	}) => Action<ChatStorePrivateState, ChatStoreContainerProps, Promise<void>>;
	bulkResolveConversationActions: (
		args: BulkResolveConversationActionsArgs,
	) => Action<ChatStorePrivateState, ChatStoreContainerProps, Promise<undefined>>;
	bulkResolveConversationUIActions: (
		args: BulkResolveConversationUIActionsArgs,
	) => Action<ChatStorePrivateState, ChatStoreContainerProps, Promise<undefined>>;
	clearFollowUpMessages: (args_0: {
		conversationId?: string;
	}) => Action<ChatStorePrivateState, ChatStoreContainerProps, Promise<void>>;
	stopTask: (args_0: {
		conversationId: string;
	}) => Action<ChatStorePrivateState, ChatStoreContainerProps, Promise<void>>;
	clearTargetTaskMessageId: ({ conversationId }: { conversationId: string }) => ChatStoreAction;
	createConversationOptimistically: (options: {
		name?: string;
		sourceId?: string;
		id?: string;
		timeCreated?: string;
		lastMessageTimestamp?: string;
		sourceAgentId?: string;
		status?: CHAT_STORE_STATUS;
		lastMessageType?: MessageType[];
		lastAgentMessageStatus?: LastAgentMessageStatus;
		agent?: AgentMetadata;
		agentVersion?: AgentVersion;
		resultAttributionContext?: ConversationResultAttributionContext;
		chatEntryPoint?: Conversation['chatEntryPoint'];
		isViewModeRemix?: boolean;
		rovoSpaceId?: string;
		scopes?: ConversationChannelScope[];
	}) => Action<ChatStorePrivateState, ChatStoreContainerProps, Promise<void>>;
	createSeededConversation: (options: {
		name?: string;
		dialogues: Dialogues[];
		sourceId?: string;
		rovoJourneyId?: string;
		id?: string;
		followUpSource: Omit<FollowUpSource, 'none'>;
		agentId?: string;
		agentVersion?: AgentVersion;
		resultAttributionContext?: ConversationResultAttributionContext;
		chatEntryPoint?: Conversation['chatEntryPoint'];
		tags?: string[];
		skipNextFetch?: boolean;
		// When false, create the chat but leave the currently selected chat unchanged.
		selectConversation?: boolean;
		// When the seeded conversation is scoped to a Rovo Space (e.g. created from
		// the SpaceLandingPage chat input or a space starter), set this so the
		// conversation is associated with the space immediately on optimistic creation.
		rovoSpaceId?: string;
		mode?: {
			deepResearchEnabled?: boolean;
			thinkDeeperEnabled?: boolean;
			fastModeEnabled?: boolean;
			taskModeEnabled?: boolean;
			webSearchEnabled?: boolean;
			sourceMode?: SourceMode;
			useCurrentPageContext?: boolean;
			spaceSourceCount?: number;
			appFilters?: RovoApp[];
		};
	}) => Action<ChatStorePrivateState, ChatStoreContainerProps, Promise<string | undefined>>;
	deleteConversation: ({ conversationId }: { conversationId: string }) => ChatStoreAction;
	getChat: (
		args_0: FetchConversationArgs,
	) => Action<ChatStorePrivateState, ChatStoreContainerProps, Promise<void>>;
	handleChatOpenPubsubAction: (args_0: {
		conversationId: string;
		agentId?: string;
	}) => Action<ChatStorePrivateState, ChatStoreContainerProps, Promise<void>>;
	hydrateConversationWithExternalParams: (args_0: {
		autoSelectConversation: boolean;
	}) => Action<ChatStorePrivateState, ChatStoreContainerProps, Promise<void>>;
	fetchConversationChannelsData: (
		args_0?: FetchConversationChannelsDataArgs | undefined,
	) => Action<ChatStorePrivateState, ChatStoreContainerProps, Promise<void>>;
	loadMoreConversationChannelsData: () => Action<
		ChatStorePrivateState,
		ChatStoreContainerProps,
		Promise<void>
	>;
	setConversationListMounted: (isConversationListMounted: boolean) => ChatStoreAction;
	setConversationData: ({
		conversationId,
		data,
	}: {
		conversationId: string;
		data: Partial<Conversation>;
	}) => ChatStoreAction;
	removeMessage: (args_0: {
		messageId: number;
		conversationId?: string;
	}) => Action<ChatStorePrivateState, ChatStoreContainerProps, Promise<void>>;
	truncateFromLastHumanMessage: (args_0: {
		conversationId?: string;
	}) => Action<ChatStorePrivateState, ChatStoreContainerProps, Promise<StoreChatItem[]>>;
	resolveConversationAction: (
		args: ResolveConversationActionArgs,
	) => Action<ChatStorePrivateState, ChatStoreContainerProps, Promise<undefined>>;
	sendA2UIAction: (
		args_0: SendA2UIActionArgs,
	) => Action<ChatStorePrivateState, ChatStoreContainerProps, Promise<void>>;
	sendMessage: (
		props: SendMessageConsumerPramas,
	) => Action<ChatStorePrivateState, ChatStoreContainerProps, Promise<void>>;
	sendMessageConfirmedTools: (args_0: {
		conversationId?: string;
		agentId?: string;
		confirmed_tools: RovoLabsConfirmedTools;
		messageSources?: Partial<MessageSource>;
		storeUserMessages?: boolean;
		webSearchEnabled?: boolean;
		deepResearchEnabled?: boolean;
		thinkDeeperEnabled?: boolean;
		alphaExtendedThinkingMode?: AlphaExtendedThinkingMode;
		fastModeEnabled?: boolean;
		taskModeEnabled?: boolean;
		includeAIFeatureContext?: boolean;
		agentVersion?: AgentVersionInput;
		actionedAnalyticsAttributes?: AdditionalAnalyticAttributes;
		resolutionType?: 'confirmed' | 'cancelled';
		onResponseComplete?: () => void;
		productKey?: string;
		addCurrentPageAsContextUserPreference?: boolean;
		browserContextSanitizer?: BrowserContextSanitizer;
		hasStagedContent?: boolean;
	}) => Action<ChatStorePrivateState, ChatStoreContainerProps, Promise<void>>;
	setSelectedConversation: (args_0: {
		conversationId: string;
		sourceAgentId?: string;
		/**
		 * When the id is not yet in `conversations` (e.g. opened from space-scoped GET list),
		 * seed the store from this payload so selection is not downgraded to empty chat.
		 */
		conversationMetadata?: Conversation & { isEmpty?: unknown; currentTask?: unknown };
		/**
		 * When set, ChatHistory will scroll to this specific message after the conversation loads.
		 * Used by Go to Chat from the Tasks List to route to the task's completion/current message.
		 */
		targetMessageId?: string | null;
	}) => Action<ChatStorePrivateState, ChatStoreContainerProps, Promise<void>>;
	updateConversation: ({
		conversationId,
		data,
	}: {
		conversationId: string;
		data: Partial<Pick<Conversation, 'name' | 'readStatus' | 'isPinned' | 'pinnedAt'>>;
	}) => ChatStoreAction;
	removeConversationFromStore: ({ conversationId }: { conversationId: string }) => ChatStoreAction;
	reconnectToInProgressStream: (
		args_0: ReconnectToInProgressStreamParams,
	) => Action<ChatStorePrivateState, ChatStoreContainerProps, Promise<void>>;
	registerHumanMessageIdReplacementHandler: (
		handler: HumanMessageIdReplacementListener,
	) => Action<ChatStorePrivateState, ChatStoreContainerProps, () => void>;
	restoreTaskModeIfNeeded: () => ChatStoreAction;
	warmConversationCache: (
		args_0: WarmConversationCacheArgs,
	) => Action<ChatStorePrivateState, ChatStoreContainerProps, Promise<void>>;
	prefetchConversationMessages: (
		args_0: PrefetchConversationMessagesArgs,
	) => Action<ChatStorePrivateState, ChatStoreContainerProps, Promise<void>>;
	seedConversation: ({
		conversationId,
		skipNextFetch,
	}: {
		conversationId: string;
		/** When true, ChatStoreContainer will skip its initial fetchConversation call.
		 * Use this when messages have already been populated locally (e.g. template flow)
		 * to prevent the fetch from overwriting locally-seeded messages. */
		skipNextFetch?: boolean;
	}) => ChatStoreAction;
} = {
	abortRequests,
	addAgentMessage,
	addErrorMessage,
	answerNow,
	bulkResolveConversationActions,
	bulkResolveConversationUIActions,
	clearFollowUpMessages,
	stopTask,
	clearTargetTaskMessageId,
	createConversationOptimistically,
	createSeededConversation,
	deleteConversation,
	getChat: fetchConversation,
	handleChatOpenPubsubAction,
	hydrateConversationWithExternalParams,
	fetchConversationChannelsData,
	loadMoreConversationChannelsData,
	setConversationListMounted,
	setConversationData,
	removeMessage,
	truncateFromLastHumanMessage,
	resolveConversationAction,
	sendA2UIAction,
	sendMessage,
	sendMessageConfirmedTools,
	setSelectedConversation,
	updateConversation,
	removeConversationFromStore,
	reconnectToInProgressStream,
	registerHumanMessageIdReplacementHandler,
	restoreTaskModeIfNeeded,
	warmConversationCache,
	prefetchConversationMessages,
	seedConversation,
};

export type ChatStoreActions = typeof actions;
